import React, { Component } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  CircularProgress,
  Divider,
  Alert,
  Card,
  CardContent,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LanguageIcon from '@mui/icons-material/Language';
import InfoIcon from '@mui/icons-material/Info';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalculateIcon from '@mui/icons-material/Calculate';
import HomeIcon from '@mui/icons-material/Home';

// Wrapper to use hooks with class component
const withRouter = WrappedComponent => props => {
  const params = useParams();
  return <WrappedComponent {...props} params={params} />;
};

class DeadlineDetails extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      error: '',
      deadlineInfo: null,
    };
  }

  componentDidMount() {
    this.fetchDeadlineInfo();
  }

  fetchDeadlineInfo = () => {
    const { state, county } = this.props.params;
    console.log('URL params:', { state, county });
    
    if (!state || !county) {
      this.setState({
        error: 'Invalid state or county information',
        loading: false,
      });
      return;
    }

    // Special handling for DC's cleaner URL
    const isDC = state.toUpperCase() === 'DC' && county.toUpperCase() === 'DC';
    
    let stateParam = state.trim().replace(/%20/g, ' ');
    // Convert underscores to spaces in county names for the new URL format
    let countyParam = county.trim().replace(/_/g, ' ').replace(/%20/g, ' ');
    
    // If using DC/DC format, convert to proper parameters for API
    if (isDC) {
      stateParam = 'DC';
      countyParam = 'District of Columbia';
    } 
    // For other states, we no longer need to add County suffix here
    // Let the backend handle various county name formats
    
    console.log('Cleaned params:', { stateParam, countyParam });
    
    const ajax_wrapper = (method, url, data, callback) => {
      console.log('Making API request to:', url);
      const xhr = new XMLHttpRequest();
      xhr.open(method, url, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          console.log('API response status:', xhr.status);
          if (xhr.status === 200) {
            callback(JSON.parse(xhr.responseText));
          } else {
            console.error('API request failed:', xhr.responseText);
            this.setState({
              error: 'Failed to fetch deadline information',
              loading: false,
            });
          }
        }
      };
      xhr.send(data ? JSON.stringify(data) : null);
    };

    ajax_wrapper(
      'GET',
      `/api/property_tax_deadline/info/?county=${encodeURIComponent(countyParam)}&state=${encodeURIComponent(stateParam)}`,
      null,
      (response) => {
        console.log('API response data:', response);
        
        // Use the county name from the API response if available (for partial matches)
        if (response.county && response.county !== countyParam) {
          console.log(`Using county name from API: ${response.county} instead of ${countyParam}`);
        }
        
        this.setState({
          deadlineInfo: response,
          loading: false,
        });
      }
    );
  };

  render() {
    const { loading, error, deadlineInfo } = this.state;

    // SEO data
    let pageTitle = 'Property Tax Appeal Deadline';
    let pageDescription = 'Find property tax appeal deadlines for your county.';

    if (deadlineInfo) {
      // Format the title appropriately for DC vs. other locations
      const isDC = deadlineInfo.state === 'DC' || deadlineInfo.state === 'District of Columbia';
      
      if (isDC) {
        pageTitle = `${deadlineInfo.county} - Property Tax Appeal Deadline`;
        pageDescription = `Property tax appeal deadline information for ${deadlineInfo.county}. File your appeal by ${deadlineInfo.filing_date}.`;
      } else {
        pageTitle = `${deadlineInfo.county}, ${deadlineInfo.state} - Property Tax Appeal Deadline`;
        pageDescription = `Property tax appeal deadline information for ${deadlineInfo.county}, ${deadlineInfo.state}. File your appeal by ${deadlineInfo.filing_date}.`;
      }
    }

    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
        </Helmet>

        <Card elevation={4} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={8}>
              <CircularProgress size={60} />
            </Box>
          ) : error ? (
            <Box p={4}>
              <Alert 
                severity="error" 
                sx={{ mb: 3 }}
                action={
                  <Button 
                    component={Link} 
                    to="/property_tax_deadline" 
                    color="inherit" 
                    size="small" 
                    startIcon={<ArrowBackIcon />}
                  >
                    Back to search
                  </Button>
                }
              >
                <Typography variant="body1" gutterBottom>
                  {error}
                </Typography>
                <Typography variant="body2">
                  URL: /property_tax_deadline/{this.props.params.state}/{this.props.params.county}
                </Typography>
              </Alert>
            </Box>
          ) : deadlineInfo ? (
            <>
              <Box 
                sx={{ 
                  bgcolor: '#4c5fd5', 
                  color: 'white', 
                  py: 3, 
                  px: 4,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  background: 'linear-gradient(90deg, #4c5fd5 0%, #5a6feb 100%)',
                }}
              >
                <CalendarTodayIcon fontSize="large" />
                <Box>
                  {deadlineInfo.state === 'DC' || deadlineInfo.state === 'District of Columbia' ? (
                    <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
                      {deadlineInfo.county}
                    </Typography>
                  ) : (
                    <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
                      {deadlineInfo.county}, {deadlineInfo.state}
                    </Typography>
                  )}
                  <Typography variant="h6">
                    Property Tax Appeal Deadline
                  </Typography>
                </Box>
              </Box>

              <CardContent sx={{ p: 4 }}>
                <Box 
                  sx={{ 
                    p: 3, 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: 2,
                    mb: 4,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid item>
                      <CalendarTodayIcon color="primary" fontSize="large" />
                    </Grid>
                    <Grid item xs>
                      <Typography variant="h6" color="#4c5fd5" gutterBottom>
                        Filing Deadline:
                      </Typography>
                      <Typography 
                        variant="h4" 
                        fontWeight="bold" 
                        color="text.primary"
                      >
                        {deadlineInfo.filing_date || 'Not specified'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                {deadlineInfo.government_website && (
                  <Box 
                    sx={{ 
                      p: 3, 
                      backgroundColor: 'rgba(76, 95, 213, 0.05)', 
                      borderRadius: 2,
                      mb: 4,
                      border: '1px solid rgba(76, 95, 213, 0.15)',
                    }}
                  >
                    <Grid container alignItems="center" spacing={2}>
                      <Grid item>
                        <LanguageIcon sx={{ color: '#4c5fd5' }} fontSize="large" />
                      </Grid>
                      <Grid item xs>
                        <Typography variant="h6" color="#4c5fd5" gutterBottom>
                          Official Government Resources:
                        </Typography>
                        <Typography variant="body1">
                          For more information please visit{' '}
                          <a 
                            href={deadlineInfo.government_website.startsWith('http') ? deadlineInfo.government_website : `https://${deadlineInfo.government_website}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{
                              color: '#4c5fd5',
                              fontWeight: 'bold',
                              textDecoration: 'none',
                            }}
                          >
                            {deadlineInfo.government_website}
                          </a>
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {deadlineInfo.notes && (
                  <Box 
                    sx={{ 
                      p: 3, 
                      backgroundColor: '#fff8e1', 
                      borderRadius: 2,
                      mb: 4,
                      border: '1px solid #ffe57f',
                    }}
                  >
                    <Grid container alignItems="flex-start" spacing={2}>
                      <Grid item>
                        <InfoIcon sx={{ color: '#ff9800' }} fontSize="large" />
                      </Grid>
                      <Grid item xs>
                        <Typography variant="h6" sx={{ color: '#e65100' }} gutterBottom>
                          Additional Information:
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#424242' }}>
                          {deadlineInfo.notes}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                <Divider sx={{ my: 4 }} />

                <Box sx={{ mt: 2 }}>
                  <Typography variant="h5" gutterBottom color="text.secondary" fontWeight="medium">
                    What's Next?
                  </Typography>
                  <Typography variant="body1" paragraph sx={{ mb: 4 }}>
                    Want to know if your property is overassessed? Check our property tax calculator 
                    to see if your property taxes are fair compared to similar properties in your area.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Button
                        component={Link}
                        to="/property_tax_calculator"
                        variant="contained"
                        color="primary"
                        fullWidth
                        sx={{ 
                          py: 1.5, 
                          fontSize: '1rem',
                          fontWeight: 'bold',
                          boxShadow: 2,
                          bgcolor: '#4c5fd5',
                          '&:hover': {
                            bgcolor: '#3f51b5',
                            boxShadow: 4,
                          }
                        }}
                        startIcon={<CalculateIcon />}
                      >
                        Property Tax Calculator
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Button
                        component={Link}
                        to="/property_tax_deadline"
                        variant="outlined"
                        fullWidth
                        sx={{ 
                          py: 1.5, 
                          fontSize: '1rem',
                          fontWeight: 'medium',
                          borderColor: '#4c5fd5',
                          color: '#4c5fd5',
                          '&:hover': {
                            borderColor: '#3f51b5',
                            color: '#3f51b5',
                          }
                        }}
                        startIcon={<ArrowBackIcon />}
                      >
                        Back to Deadline Search
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Button
                        component="a"
                        href="https://yourhomebase.co/"
                        variant="contained"
                        color="primary"
                        fullWidth
                        sx={{ 
                          py: 1.5, 
                          fontSize: '1rem',
                          fontWeight: 'bold',
                          boxShadow: 2,
                          bgcolor: '#4c5fd5',
                          '&:hover': {
                            bgcolor: '#3f51b5',
                            boxShadow: 4,
                          }
                        }}
                        startIcon={<HomeIcon />}
                      >
                        Visit Homepage
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </>
          ) : (
            <Box p={4}>
              <Alert 
                severity="warning"
                action={
                  <Button 
                    component={Link} 
                    to="/property_tax_deadline" 
                    color="inherit" 
                    size="small"
                    startIcon={<ArrowBackIcon />}
                  >
                    Back to search
                  </Button>
                }
              >
                No deadline information found
              </Alert>
            </Box>
          )}
        </Card>
      </Container>
    );
  }
}

export default withRouter(DeadlineDetails); 